// Feature: features/nma-appeal-templates-demo-ui.md
import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Select,
  Input,
  Textarea,
  Button,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  HStack,
  VStack,
  Divider,
  Switch,
  FormControl,
  FormLabel,
  useDisclosure,
  useToast,
  Collapse,
} from '@chakra-ui/react';
import {
  AddIcon,
  EditIcon,
  DeleteIcon,
  CopyIcon,
  RepeatIcon,
  AttachmentIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
} from '@chakra-ui/icons';
import {
  PAYER_TEMPLATE_OPTIONS,
  PROCEDURE_TYPE_OPTIONS,
  DENIAL_SCENARIO_OPTIONS,
  FILE_ROLE_OPTIONS,
  TEMPLATE_PLACEHOLDERS,
} from './appealTemplatesStore';

const COMMON_IONM_CPTS = [
  '95938',
  '95939',
  '95940',
  '95941',
  'G0453',
  '95861',
  '95870',
  '95822',
  '95955',
  '95867',
  '95868',
  '95925',
  '95926',
  '51785',
];

const getPayerBadgeColor = (payer) => {
  const p = String(payer || '').toUpperCase();
  if (p === 'ALL PAYERS') return 'gray';
  if (p.includes('CIGNA')) return 'orange';
  if (p.includes('AETNA')) return 'purple';
  if (p.includes('UHC') || p.includes('UNITED')) return 'blue';
  if (p.includes('MEDICARE')) return 'teal';
  if (p.includes('BCBS')) return 'cyan';
  return 'pink';
};

const getProcedureBadgeColor = (proc) => {
  const p = String(proc || '').toUpperCase();
  if (p === 'ALL PROCEDURES') return 'gray';
  if (p.includes('CERVICAL')) return 'cyan';
  if (p.includes('LUMBAR') || p.includes('THORACIC')) return 'blue';
  if (p.includes('SCOLIOSIS') || p.includes('DEFORMITY')) return 'orange';
  if (p.includes('CRANI')) return 'purple';
  if (p.includes('ENT') || p.includes('THYROID')) return 'pink';
  if (p.includes('VASCULAR') || p.includes('CAROTID')) return 'red';
  return 'teal';
};

const EMPTY_FORM = {
  id: '',
  name: '',
  description: '',
  payer_match: 'CIGNA',
  procedure_type: 'Thoracic / Lumbar Spine (TLIF / ALIF / Decompression)',
  denial_scenario: 'Medical Necessity / Experimental',
  claim_type: 'pro',
  cpt_codes_str: '95938, 95939, 95941',
  is_base_template: false,
  is_active: true,
  template_text: '',
  attached_files: [],
};

const NMAAppealTemplatesTab = ({
  templates = [],
  onSaveTemplates,
  onResetDefaults,
}) => {
  const toast = useToast();
  const fileInputRef = useRef(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [payerFilter, setPayerFilter] = useState('');
  const [procedureFilter, setProcedureFilter] = useState('');
  const [scenarioFilter, setScenarioFilter] = useState('');
  const [expandedTextMap, setExpandedTextMap] = useState({});

  // Create / Edit Modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingTemplate, setEditingTemplate] = useState(EMPTY_FORM);
  const [isNewTemplate, setIsNewTemplate] = useState(true);
  const [pendingFileRole, setPendingFileRole] = useState('Winning Appeal Sample');
  const [pendingFileNotes, setPendingFileNotes] = useState('');

  const filteredTemplates = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (templates || []).filter((tpl) => {
      if (payerFilter && tpl.payer_match !== payerFilter) return false;
      if (procedureFilter && tpl.procedure_type !== procedureFilter) return false;
      if (scenarioFilter && tpl.denial_scenario !== scenarioFilter) return false;
      if (q) {
        const hay = [
          tpl.name,
          tpl.description,
          tpl.payer_match,
          tpl.procedure_type,
          tpl.denial_scenario,
          (tpl.cpt_codes || []).join(' '),
          tpl.template_text,
          (tpl.attached_files || []).map((f) => `${f.name} ${f.notes || ''}`).join(' '),
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [templates, payerFilter, procedureFilter, scenarioFilter, searchQuery]);

  const kpiStats = useMemo(() => {
    const total = templates.length;
    const active = templates.filter((t) => t.is_active !== false).length;
    const payerScoped = templates.filter((t) => t.payer_match && t.payer_match !== 'All Payers').length;
    const procScoped = templates.filter((t) => t.procedure_type && t.procedure_type !== 'All Procedures').length;
    const totalFiles = templates.reduce((acc, t) => acc + (t.attached_files?.length || 0), 0);
    return { total, active, payerScoped, procScoped, totalFiles };
  }, [templates]);

  const handleOpenNewModal = () => {
    setIsNewTemplate(true);
    setEditingTemplate({
      ...EMPTY_FORM,
      id: `tpl-custom-${Date.now()}`,
      template_text: `CLINICAL & POLICY APPEAL GUIDANCE (Claim {{CLAIM_NUMBER}}):\n- Payer: {{PAYER_NAME}} | Procedure: {{PROCEDURE_TYPE}} | DOS: {{DOS}}\n- Disputed CPTs: {{DENIED_CPTS}} (CARC: {{TOP_CARC}})\n- Primary Argument: Cite primary diagnosis {{PRIMARY_DX}} (All DX: {{ALL_DX}}) and operative monitoring findings from {{FACILITY_NAME}} to overturn "{{DENIAL_REASON}}".`,
      attached_files: [],
    });
    setPendingFileNotes('');
    onOpen();
  };

  const handleOpenEditModal = (tpl) => {
    setIsNewTemplate(false);
    setEditingTemplate({
      ...tpl,
      cpt_codes_str: (tpl.cpt_codes || []).join(', '),
      attached_files: [...(tpl.attached_files || [])],
    });
    setPendingFileNotes('');
    onOpen();
  };

  const handleDuplicateTemplate = (tpl) => {
    const copy = {
      ...tpl,
      id: `tpl-copy-${Date.now()}`,
      name: `${tpl.name} (Copy)`,
      is_base_template: false,
      updated_at: new Date().toISOString().slice(0, 10),
    };
    onSaveTemplates([copy, ...templates]);
    toast({
      title: 'Template Duplicated',
      description: `Created "${copy.name}".`,
      status: 'success',
      duration: 2500,
      isClosable: true,
    });
  };

  const handleDeleteTemplate = (tplId, tplName) => {
    const next = templates.filter((t) => t.id !== tplId);
    onSaveTemplates(next);
    toast({
      title: 'Template Removed',
      description: `Removed "${tplName}" from the library.`,
      status: 'info',
      duration: 2500,
      isClosable: true,
    });
  };

  const handleToggleActive = (tplId) => {
    const next = templates.map((t) =>
      t.id === tplId ? { ...t, is_active: t.is_active === false } : t
    );
    onSaveTemplates(next);
  };

  const handleToggleCptInForm = (cpt) => {
    const currentList = String(editingTemplate.cpt_codes_str || '')
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    const exists = currentList.includes(cpt);
    const nextList = exists ? currentList.filter((c) => c !== cpt) : [...currentList, cpt];
    setEditingTemplate((prev) => ({
      ...prev,
      cpt_codes_str: nextList.join(', '),
    }));
  };

  const handleInsertPlaceholder = (token) => {
    setEditingTemplate((prev) => ({
      ...prev,
      template_text: `${prev.template_text || ''}${prev.template_text?.endsWith(' ') || !prev.template_text ? '' : ' '}${token}`,
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    files.forEach((file) => {
      const sizeKb = Math.max(1, Math.round(file.size / 1024));
      const isTextFile =
        file.name.endsWith('.txt') ||
        file.name.endsWith('.md') ||
        file.type === 'text/plain' ||
        file.type === 'text/markdown';

      if (isTextFile) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const textContent = String(ev.target?.result || '').trim();
          const newFileEntry = {
            id: `file-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            name: file.name,
            size_kb: sizeKb,
            role: pendingFileRole,
            notes: pendingFileNotes || `Uploaded text template (${sizeKb} KB)`,
            extracted_text: textContent.slice(0, 4000),
          };
          setEditingTemplate((prev) => ({
            ...prev,
            attached_files: [...(prev.attached_files || []), newFileEntry],
            template_text: prev.template_text
              ? `${prev.template_text}\n\n--- [Imported from ${file.name}] ---\n${textContent.slice(0, 2000)}`
              : textContent.slice(0, 2000),
          }));
        };
        reader.readAsText(file);
      } else {
        const newFileEntry = {
          id: `file-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          name: file.name,
          size_kb: sizeKb,
          role: pendingFileRole,
          notes: pendingFileNotes || `Uploaded reference file (${file.type || 'document'})`,
        };
        setEditingTemplate((prev) => ({
          ...prev,
          attached_files: [...(prev.attached_files || []), newFileEntry],
        }));
      }
    });

    setPendingFileNotes('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast({
      title: 'File(s) Attached to Template',
      description: `Added ${files.map((f) => f.name).join(', ')}.`,
      status: 'success',
      duration: 2500,
      isClosable: true,
    });
  };

  const handleRemoveAttachedFile = (fileId) => {
    setEditingTemplate((prev) => ({
      ...prev,
      attached_files: (prev.attached_files || []).filter((f) => f.id !== fileId),
    }));
  };

  const handleSaveModal = () => {
    if (!editingTemplate.name || !editingTemplate.name.trim()) {
      toast({
        title: 'Template Name Required',
        description: 'Please enter a descriptive name for the template.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const parsedCpts = String(editingTemplate.cpt_codes_str || '')
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);

    const recordToSave = {
      id: editingTemplate.id || `tpl-custom-${Date.now()}`,
      name: editingTemplate.name.trim(),
      description: (editingTemplate.description || '').trim(),
      payer_match: editingTemplate.payer_match || 'All Payers',
      procedure_type: editingTemplate.procedure_type || 'All Procedures',
      denial_scenario: editingTemplate.denial_scenario || 'All Scenarios',
      claim_type: editingTemplate.claim_type || 'pro',
      cpt_codes: parsedCpts,
      is_base_template: Boolean(editingTemplate.is_base_template),
      is_active: editingTemplate.is_active !== false,
      updated_at: new Date().toISOString().slice(0, 10),
      template_text: editingTemplate.template_text || '',
      attached_files: editingTemplate.attached_files || [],
    };

    const existsIdx = templates.findIndex((t) => t.id === recordToSave.id);
    let nextTemplates;
    if (existsIdx >= 0) {
      nextTemplates = [...templates];
      nextTemplates[existsIdx] = recordToSave;
    } else {
      nextTemplates = [recordToSave, ...templates];
    }

    onSaveTemplates(nextTemplates);
    toast({
      title: isNewTemplate ? 'Template Created' : 'Template Updated',
      description: `"${recordToSave.name}" is now available for automatic claim matching.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    onClose();
  };

  return (
    <Box>
      {/* Top KPI & Action Strip */}
      <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4} mb={4}>
        <Card variant="outline" bg="white" borderTop="3px solid #3182CE">
          <CardBody py={3} px={4}>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
              Active Appeal Templates
            </Text>
            <Text fontSize="2xl" fontWeight="extrabold" color="blue.600">
              {kpiStats.active} / {kpiStats.total}
            </Text>
            <Text fontSize="xs" color="gray.600">
              Auto-matched when queueing appeals or eAppeals
            </Text>
          </CardBody>
        </Card>

        <Card variant="outline" bg="white" borderTop="3px solid #DD6B20">
          <CardBody py={3} px={4}>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
              Payer-Specific Playbooks
            </Text>
            <Text fontSize="2xl" fontWeight="extrabold" color="orange.600">
              {kpiStats.payerScoped}
            </Text>
            <Text fontSize="xs" color="gray.600">
              Cigna 0509, Aetna CPB 0697, UHC/Optum, CMS LCD
            </Text>
          </CardBody>
        </Card>

        <Card variant="outline" bg="white" borderTop="3px solid #805AD5">
          <CardBody py={3} px={4}>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
              Procedure-Scoped Templates
            </Text>
            <Text fontSize="2xl" fontWeight="extrabold" color="purple.600">
              {kpiStats.procScoped}
            </Text>
            <Text fontSize="xs" color="gray.600">
              Cervical, Thoracolumbar, Craniotomy, ENT, Vascular
            </Text>
          </CardBody>
        </Card>

        <Card variant="outline" bg="white" borderTop="3px solid #38A169">
          <CardBody py={3} px={4}>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
              Attached Reference Files
            </Text>
            <Text fontSize="2xl" fontWeight="extrabold" color="green.600">
              {kpiStats.totalFiles} Files
            </Text>
            <Text fontSize="xs" color="gray.600">
              Policy PDFs, winning samples, AAN/ACNS guidelines
            </Text>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Filter & Create Template Bar */}
      <Card variant="outline" bg="white" mb={4}>
        <CardBody py={3} px={4}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <Flex gap={2.5} wrap="wrap" align="center" flex="1">
              <Input
                size="sm"
                maxW="260px"
                placeholder="Search templates, CPTs, policies, files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Select
                size="sm"
                maxW="210px"
                placeholder="All Payers"
                value={payerFilter}
                onChange={(e) => setPayerFilter(e.target.value)}
              >
                {PAYER_TEMPLATE_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
              <Select
                size="sm"
                maxW="240px"
                placeholder="All Procedure Types"
                value={procedureFilter}
                onChange={(e) => setProcedureFilter(e.target.value)}
              >
                {PROCEDURE_TYPE_OPTIONS.map((proc) => (
                  <option key={proc} value={proc}>
                    {proc}
                  </option>
                ))}
              </Select>
              <Select
                size="sm"
                maxW="220px"
                placeholder="All Denial Scenarios"
                value={scenarioFilter}
                onChange={(e) => setScenarioFilter(e.target.value)}
              >
                {DENIAL_SCENARIO_OPTIONS.map((sc) => (
                  <option key={sc} value={sc}>
                    {sc}
                  </option>
                ))}
              </Select>
              {(searchQuery || payerFilter || procedureFilter || scenarioFilter) && (
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    setSearchQuery('');
                    setPayerFilter('');
                    setProcedureFilter('');
                    setScenarioFilter('');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Flex>

            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                colorScheme="gray"
                leftIcon={<RepeatIcon />}
                onClick={() => {
                  onResetDefaults();
                  toast({
                    title: 'Demo Templates Reset',
                    description: 'Restored default NMA payer & procedure templates.',
                    status: 'info',
                    duration: 2500,
                    isClosable: true,
                  });
                }}
              >
                Reset Demo Defaults
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<AddIcon />}
                onClick={handleOpenNewModal}
              >
                + Add / Upload Template
              </Button>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Templates Grid / List */}
      <VStack align="stretch" spacing={3.5}>
        {filteredTemplates.map((tpl) => {
          const isExpanded = Boolean(expandedTextMap[tpl.id]);
          return (
            <Card
              key={tpl.id}
              variant="outline"
              bg={tpl.is_active === false ? 'gray.50' : 'white'}
              borderLeft="4px solid"
              borderLeftColor={
                tpl.is_base_template
                  ? 'teal.500'
                  : tpl.is_active === false
                  ? 'gray.300'
                  : 'blue.500'
              }
              shadow="xs"
            >
              <CardBody py={3.5} px={4}>
                <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
                  <Box flex="1" minW="280px">
                    <HStack spacing={2} wrap="wrap" mb={1}>
                      <Heading size="sm" color="gray.800">
                        {tpl.name}
                      </Heading>
                      {tpl.is_base_template && (
                        <Badge colorScheme="teal" variant="solid" fontSize="10px">
                          BASE LETTERHEAD LAYER
                        </Badge>
                      )}
                      <Badge
                        colorScheme={tpl.is_active === false ? 'gray' : 'green'}
                        variant="subtle"
                        fontSize="10px"
                        cursor="pointer"
                        onClick={() => handleToggleActive(tpl.id)}
                      >
                        {tpl.is_active === false ? 'Inactive (Click to Enable)' : 'Active'}
                      </Badge>
                    </HStack>

                    <Text fontSize="xs" color="gray.600" mb={2}>
                      {tpl.description}
                    </Text>

                    {/* Matching Scope Tags */}
                    <HStack spacing={2} wrap="wrap" mb={2.5}>
                      <Badge colorScheme={getPayerBadgeColor(tpl.payer_match)} fontSize="10px" px={2} py={0.5}>
                        Payer: {tpl.payer_match}
                      </Badge>
                      <Badge colorScheme={getProcedureBadgeColor(tpl.procedure_type)} fontSize="10px" px={2} py={0.5}>
                        Procedure: {tpl.procedure_type}
                      </Badge>
                      {tpl.denial_scenario && tpl.denial_scenario !== 'All Scenarios' && (
                        <Badge colorScheme="purple" variant="outline" fontSize="10px" px={2} py={0.5}>
                          Scenario: {tpl.denial_scenario}
                        </Badge>
                      )}
                      <Badge colorScheme="gray" fontSize="10px">
                        Claim Type: {(tpl.claim_type || 'pro').toUpperCase()}
                      </Badge>
                      {(tpl.cpt_codes || []).length > 0 ? (
                        <HStack spacing={1}>
                          <Text fontSize="10px" color="gray.500" fontWeight="bold">
                            CPTs:
                          </Text>
                          {tpl.cpt_codes.map((c) => (
                            <Badge key={c} colorScheme="red" variant="subtle" fontSize="10px">
                              {c}
                            </Badge>
                          ))}
                        </HStack>
                      ) : (
                        <Badge colorScheme="gray" variant="subtle" fontSize="10px">
                          All IONM CPTs
                        </Badge>
                      )}
                    </HStack>

                    {/* Attached Reference Files */}
                    {(tpl.attached_files || []).length > 0 && (
                      <Box bg="gray.50" p={2} borderRadius="md" borderWidth="1px" borderColor="gray.200" mb={2}>
                        <Text fontSize="10px" fontWeight="bold" color="gray.600" textTransform="uppercase" mb={1}>
                          Attached Reference Files &amp; Policies ({tpl.attached_files.length}):
                        </Text>
                        <Flex gap={2} wrap="wrap">
                          {tpl.attached_files.map((f) => (
                            <HStack
                              key={f.id || f.name}
                              spacing={1.5}
                              bg="white"
                              px={2.5}
                              py={1}
                              borderRadius="sm"
                              borderWidth="1px"
                              borderColor="blue.200"
                            >
                              <AttachmentIcon boxSize={3} color="blue.600" />
                              <Text fontSize="11px" fontWeight="semibold" color="blue.800">
                                {f.name}
                              </Text>
                              <Badge colorScheme="blue" variant="subtle" fontSize="9px">
                                {f.role}
                              </Badge>
                              {f.size_kb && (
                                <Text fontSize="10px" color="gray.500">
                                  ({f.size_kb} KB)
                                </Text>
                              )}
                            </HStack>
                          ))}
                        </Flex>
                      </Box>
                    )}

                    {/* Collapsible Template Text Preview */}
                    <Box>
                      <Button
                        size="xs"
                        variant="link"
                        colorScheme="blue"
                        rightIcon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        onClick={() =>
                          setExpandedTextMap((prev) => ({
                            ...prev,
                            [tpl.id]: !prev[tpl.id],
                          }))
                        }
                      >
                        {isExpanded ? 'Hide Pasted Template Text / Instructions' : 'Preview Pasted Template Text & Variables'}
                      </Button>
                      <Collapse in={isExpanded} animateOpacity>
                        <Box
                          mt={2}
                          p={3}
                          bg="gray.900"
                          color="gray.100"
                          borderRadius="md"
                          fontSize="xs"
                          fontFamily="mono"
                          whiteSpace="pre-wrap"
                          lineHeight="1.6"
                        >
                          {tpl.template_text}
                        </Box>
                      </Collapse>
                    </Box>
                  </Box>

                  {/* Card Actions */}
                  <HStack spacing={1.5}>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      variant="outline"
                      leftIcon={<EditIcon />}
                      onClick={() => handleOpenEditModal(tpl)}
                    >
                      Edit / Files
                    </Button>
                    <Button
                      size="xs"
                      colorScheme="gray"
                      variant="outline"
                      leftIcon={<CopyIcon />}
                      onClick={() => handleDuplicateTemplate(tpl)}
                    >
                      Duplicate
                    </Button>
                    {!tpl.is_base_template && (
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => handleDeleteTemplate(tpl.id, tpl.name)}
                      >
                        <DeleteIcon />
                      </Button>
                    )}
                  </HStack>
                </Flex>
              </CardBody>
            </Card>
          );
        })}
      </VStack>

      {/* Create / Edit / Upload Template Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg="blue.800" color="white" borderTopRadius="md" py={3.5}>
            {isNewTemplate ? 'Add / Upload Appeal Template' : `Edit Template: ${editingTemplate.name}`}
          </ModalHeader>
          <ModalCloseButton color="white" />

          <ModalBody py={4} bg="gray.50">
            <VStack spacing={4} align="stretch">
              {/* Section 1: Matching Rules */}
              <Card variant="outline" bg="white">
                <CardHeader py={2.5} px={4} borderBottom="1px solid" borderColor="gray.100">
                  <Heading size="xs" color="blue.800" textTransform="uppercase">
                    1. Template Identity &amp; Auto-Matching Rules (Payer / Procedure / CPTs)
                  </Heading>
                </CardHeader>
                <CardBody py={3} px={4}>
                  <VStack spacing={3} align="stretch">
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                      <Box gridColumn={{ md: 'span 2' }}>
                        <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
                          Template Name *
                        </Text>
                        <Input
                          size="sm"
                          placeholder="e.g., Cigna Policy 0509 — Cervical Spine ACDF Appeal"
                          value={editingTemplate.name}
                          onChange={(e) =>
                            setEditingTemplate((prev) => ({ ...prev, name: e.target.value }))
                          }
                        />
                      </Box>
                      <FormControl display="flex" alignItems="center" pt={5}>
                        <FormLabel htmlFor="tpl-active-switch" mb="0" fontSize="xs" fontWeight="bold">
                          Active in Auto-Matcher
                        </FormLabel>
                        <Switch
                          id="tpl-active-switch"
                          colorScheme="green"
                          isChecked={editingTemplate.is_active !== false}
                          onChange={(e) =>
                            setEditingTemplate((prev) => ({ ...prev, is_active: e.target.checked }))
                          }
                        />
                      </FormControl>
                    </SimpleGrid>

                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
                        Short Description / When to Use
                      </Text>
                      <Input
                        size="sm"
                        placeholder="Describe what denial pattern or clinical scenario this template addresses..."
                        value={editingTemplate.description}
                        onChange={(e) =>
                          setEditingTemplate((prev) => ({ ...prev, description: e.target.value }))
                        }
                      />
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
                          Target Payer
                        </Text>
                        <Select
                          size="sm"
                          value={editingTemplate.payer_match}
                          onChange={(e) =>
                            setEditingTemplate((prev) => ({ ...prev, payer_match: e.target.value }))
                          }
                        >
                          {PAYER_TEMPLATE_OPTIONS.map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </Select>
                      </Box>

                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
                          Procedure Type
                        </Text>
                        <Select
                          size="sm"
                          value={editingTemplate.procedure_type}
                          onChange={(e) =>
                            setEditingTemplate((prev) => ({
                              ...prev,
                              procedure_type: e.target.value,
                            }))
                          }
                        >
                          {PROCEDURE_TYPE_OPTIONS.map((proc) => (
                            <option key={proc} value={proc}>
                              {proc}
                            </option>
                          ))}
                        </Select>
                      </Box>

                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
                          Denial Scenario
                        </Text>
                        <Select
                          size="sm"
                          value={editingTemplate.denial_scenario}
                          onChange={(e) =>
                            setEditingTemplate((prev) => ({
                              ...prev,
                              denial_scenario: e.target.value,
                            }))
                          }
                        >
                          {DENIAL_SCENARIO_OPTIONS.map((sc) => (
                            <option key={sc} value={sc}>
                              {sc}
                            </option>
                          ))}
                        </Select>
                      </Box>

                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
                          Claim Type
                        </Text>
                        <Select
                          size="sm"
                          value={editingTemplate.claim_type}
                          onChange={(e) =>
                            setEditingTemplate((prev) => ({ ...prev, claim_type: e.target.value }))
                          }
                        >
                          <option value="pro">Professional (Pro)</option>
                          <option value="tech">Technical (Tech)</option>
                          <option value="both">Both (Pro &amp; Tech)</option>
                        </Select>
                      </Box>
                    </SimpleGrid>

                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
                        Target CPT Codes (click chips to toggle, or leave blank for all CPTs)
                      </Text>
                      <Input
                        size="sm"
                        mb={1.5}
                        placeholder="e.g., 95938, 95939, 95941"
                        value={editingTemplate.cpt_codes_str}
                        onChange={(e) =>
                          setEditingTemplate((prev) => ({ ...prev, cpt_codes_str: e.target.value }))
                        }
                      />
                      <HStack spacing={1.5} wrap="wrap">
                        {COMMON_IONM_CPTS.map((cpt) => {
                          const isSelected = String(editingTemplate.cpt_codes_str || '')
                            .toUpperCase()
                            .includes(cpt);
                          return (
                            <Badge
                              key={cpt}
                              colorScheme={isSelected ? 'red' : 'gray'}
                              variant={isSelected ? 'solid' : 'outline'}
                              cursor="pointer"
                              fontSize="10px"
                              px={2}
                              py={0.5}
                              onClick={() => handleToggleCptInForm(cpt)}
                            >
                              {isSelected ? `✓ ${cpt}` : `+ ${cpt}`}
                            </Badge>
                          );
                        })}
                      </HStack>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>

              {/* Section 2: Pasted Text & Prompt Instructions */}
              <Card variant="outline" bg="white">
                <CardHeader py={2.5} px={4} borderBottom="1px solid" borderColor="gray.100">
                  <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                    <Heading size="xs" color="blue.800" textTransform="uppercase">
                      2. Pasted Appeal Text, Clinical Argument &amp; Prompt Instructions
                    </Heading>
                    <Text fontSize="11px" color="gray.500">
                      Click any variable chip below to insert it into the template
                    </Text>
                  </Flex>
                </CardHeader>
                <CardBody py={3} px={4}>
                  <HStack spacing={1.5} wrap="wrap" mb={2.5}>
                    {TEMPLATE_PLACEHOLDERS.map((ph) => (
                      <Badge
                        key={ph.token}
                        colorScheme="blue"
                        variant="subtle"
                        cursor="pointer"
                        fontSize="10px"
                        px={2}
                        py={0.5}
                        _hover={{ bg: 'blue.100' }}
                        onClick={() => handleInsertPlaceholder(ph.token)}
                      >
                        + {ph.token} ({ph.label})
                      </Badge>
                    ))}
                  </HStack>
                  <Textarea
                    rows={8}
                    fontSize="xs"
                    fontFamily="mono"
                    placeholder="Paste winning appeal letter text, policy citations, or structured instructions for the appeal generator..."
                    value={editingTemplate.template_text}
                    onChange={(e) =>
                      setEditingTemplate((prev) => ({ ...prev, template_text: e.target.value }))
                    }
                  />
                </CardBody>
              </Card>

              {/* Section 3: File Upload / Reference Attachments */}
              <Card variant="outline" bg="white">
                <CardHeader py={2.5} px={4} borderBottom="1px solid" borderColor="gray.100">
                  <Heading size="xs" color="blue.800" textTransform="uppercase">
                    3. Upload Reference Files (Policy PDFs, Winning Samples, Society Guidelines)
                  </Heading>
                </CardHeader>
                <CardBody py={3} px={4}>
                  <VStack align="stretch" spacing={3}>
                    <Flex
                      p={3}
                      bg="blue.50"
                      borderRadius="md"
                      border="1px dashed"
                      borderColor="blue.300"
                      align="center"
                      justify="space-between"
                      wrap="wrap"
                      gap={3}
                    >
                      <HStack spacing={3} wrap="wrap" flex="1">
                        <Box minW="190px">
                          <Text fontSize="10px" fontWeight="bold" color="blue.800" mb={0.5}>
                            File Role / Type
                          </Text>
                          <Select
                            size="xs"
                            bg="white"
                            value={pendingFileRole}
                            onChange={(e) => setPendingFileRole(e.target.value)}
                          >
                            {FILE_ROLE_OPTIONS.map((role) => (
                              <option key={role} value={role}>
                                {role}
                              </option>
                            ))}
                          </Select>
                        </Box>
                        <Box flex="1" minW="200px">
                          <Text fontSize="10px" fontWeight="bold" color="blue.800" mb={0.5}>
                            File Note / Citation Hint (optional)
                          </Text>
                          <Input
                            size="xs"
                            bg="white"
                            placeholder="e.g., Page 4 covered ICD-10 table or redacted winning letter..."
                            value={pendingFileNotes}
                            onChange={(e) => setPendingFileNotes(e.target.value)}
                          />
                        </Box>
                      </HStack>

                      <Box>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept=".pdf,.doc,.docx,.txt,.md"
                          style={{ display: 'none' }}
                          onChange={handleFileUpload}
                        />
                        <Button
                          size="sm"
                          colorScheme="blue"
                          leftIcon={<AttachmentIcon />}
                          onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        >
                          Choose File(s) to Attach
                        </Button>
                      </Box>
                    </Flex>

                    {(editingTemplate.attached_files || []).length > 0 && (
                      <VStack align="stretch" spacing={1.5}>
                        {editingTemplate.attached_files.map((f) => (
                          <Flex
                            key={f.id || f.name}
                            justify="space-between"
                            align="center"
                            p={2}
                            bg="gray.50"
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor="gray.200"
                          >
                            <HStack spacing={2}>
                              <CheckCircleIcon color="green.500" boxSize={3.5} />
                              <Text fontSize="xs" fontWeight="bold" color="gray.800">
                                {f.name}
                              </Text>
                              <Badge colorScheme="blue" fontSize="10px">
                                {f.role}
                              </Badge>
                              {f.size_kb && (
                                <Text fontSize="10px" color="gray.500">
                                  ({f.size_kb} KB)
                                </Text>
                              )}
                              {f.notes && (
                                <Text fontSize="11px" color="gray.600" isTruncated maxW="320px">
                                  — {f.notes}
                                </Text>
                              )}
                            </HStack>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => handleRemoveAttachedFile(f.id)}
                            >
                              Remove
                            </Button>
                          </Flex>
                        ))}
                      </VStack>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </ModalBody>

          <ModalFooter bg="white" borderTop="1px solid" borderColor="gray.200">
            <HStack spacing={2}>
              <Button size="sm" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button size="sm" colorScheme="blue" onClick={handleSaveModal}>
                {isNewTemplate ? 'Save New Template' : 'Save Template Changes'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default NMAAppealTemplatesTab;
